import { useEffect, useState } from "react";

export interface AnchorRect {
  top: number;
  left: number;
  width: number;
  height: number;
}

interface UseTourAnchorOptions {
  targetId?: string;
  selector?: string;
  /** Re-poll for the element until found, in ms (e.g. for mounts after route change) */
  pollIntervalMs?: number;
  /** Stop polling after this duration */
  pollTimeoutMs?: number;
  /** Disable resolution (e.g. tour closed) */
  enabled?: boolean;
}

/**
 * Resolve a `data-tour-id` (preferred) or CSS selector to a live DOMRect.
 * Re-measures on resize, scroll, and DOM mutations. Polls briefly for elements
 * that mount after a navigation.
 */
export const useTourAnchor = ({
  targetId,
  selector,
  pollIntervalMs = 200,
  pollTimeoutMs = 4000,
  enabled = true,
}: UseTourAnchorOptions): { rect: AnchorRect | null; element: HTMLElement | null } => {
  const [element, setElement] = useState<HTMLElement | null>(null);
  const [rect, setRect] = useState<AnchorRect | null>(null);

  // Resolve element (with polling for late-mounted nodes)
  useEffect(() => {
    if (!enabled || (!targetId && !selector)) {
      setElement(null);
      return;
    }

    let cancelled = false;
    const start = Date.now();

    const tryResolve = () => {
      if (cancelled) return;
      const found = (targetId
        ? document.querySelector<HTMLElement>(`[data-tour-id="${targetId}"]`)
        : selector
          ? document.querySelector<HTMLElement>(selector)
          : null);

      if (found) {
        setElement(found);
      } else if (Date.now() - start < pollTimeoutMs) {
        setTimeout(tryResolve, pollIntervalMs);
      } else {
        setElement(null);
      }
    };

    tryResolve();
    return () => {
      cancelled = true;
    };
  }, [targetId, selector, enabled, pollIntervalMs, pollTimeoutMs]);

  // Measure rect on changes
  useEffect(() => {
    if (!element) {
      setRect(null);
      return;
    }

    const measure = () => {
      const r = element.getBoundingClientRect();
      setRect({
        top: r.top,
        left: r.left,
        width: r.width,
        height: r.height,
      });
    };

    measure();

    const ro = new ResizeObserver(measure);
    ro.observe(element);

    const mo = new MutationObserver(measure);
    mo.observe(document.body, { subtree: true, childList: true, attributes: true });

    window.addEventListener("scroll", measure, true);
    window.addEventListener("resize", measure);

    return () => {
      ro.disconnect();
      mo.disconnect();
      window.removeEventListener("scroll", measure, true);
      window.removeEventListener("resize", measure);
    };
  }, [element]);

  return { rect, element };
};
